import React from 'react';
import {
    Award,
    ChevronDown,
    ChevronUp,
    EyeOff,
    Plus,
    Search,
    Shapes,
    Trophy
} from 'lucide-react';
import { observer } from 'mobx-react';
import { useWrapRequest } from 'use-wrap-request';
import { arrayMove } from '@dnd-kit/sortable';
import { Spinner } from '@components/spinner';
import { Badge } from '@components/ui/badge';
import { Button } from '@components/ui/button';
import { Collapsible, CollapsibleContent } from '@components/ui/collapsible';
import { Input } from '@components/ui/input';
import { Skeleton } from '@components/ui/skeleton';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger
} from '@components/ui/tooltip';
import { cn } from '@core/utils/cn';
import {
    BadgeCategoryAdminDto,
    BadgeTypeAdminDto,
    BadgeLevelAdminDto,
    CdnObjectDto
} from '@api/FitnessActivityClient';
import { Api } from '../core/api';
import { toast } from '../core/toast';
import { injectTSDI } from '../core/tsdi';
import { getCdnImage } from '../components/utils/cdn-image';
import { getLocalizedName } from './badge-utils';

export type BadgeNodeType = 'category' | 'type' | 'level';

export interface BadgeTreeNode {
    id: string;
    label: string;
    nodeType: BadgeNodeType;
    entityId: number;
    parentId?: number;
    children?: BadgeTreeNode[];
    data?: BadgeCategoryAdminDto | BadgeTypeAdminDto | BadgeLevelAdminDto;
    enabled?: boolean;
}

export interface BadgeTreeHandle {
    updateNode: (
        nodeId: string,
        data: BadgeCategoryAdminDto | BadgeTypeAdminDto | BadgeLevelAdminDto
    ) => void;
    addNode: (node: BadgeTreeNode, parentId?: string) => void;
    removeNode: (nodeId: string) => void;
    selectNode: (nodeId: string) => void;
}

interface BadgeTreeProps {
    onSelect: (node: BadgeTreeNode | null) => void;
    onCreateRequest: (nodeType: BadgeNodeType, parentId?: number) => void;
    selectedNodeId?: string;
}

const getNodeImage = (node: BadgeTreeNode): CdnObjectDto | undefined => {
    if (node.nodeType === 'level') {
        return (node.data as BadgeLevelAdminDto)?.image || undefined;
    }
    return undefined;
};

const nodeStyles = {
    category: {
        icon: Shapes,
        iconColor: 'text-primary',
        iconBg: 'bg-primary/15 rounded-md',
        selectedBg: 'bg-primary/10',
        selectedBorder: 'border-l-primary',
        hoverBg: 'hover:bg-primary/5',
        label: 'Category'
    },
    type: {
        icon: Trophy,
        iconColor: 'text-warning',
        iconBg: 'bg-warning/15 rounded-full',
        selectedBg: 'bg-warning/10',
        selectedBorder: 'border-l-warning',
        hoverBg: 'hover:bg-warning/5',
        label: 'Type'
    },
    level: {
        icon: Award,
        iconColor: 'text-info',
        iconBg: 'bg-info/15 rounded-full',
        selectedBg: 'bg-info/10',
        selectedBorder: 'border-l-info',
        hoverBg: 'hover:bg-info/5',
        label: 'Level'
    }
};

const indentPx = (depth: number) => 8 + depth * 20;

interface TreeItemProps {
    node: BadgeTreeNode;
    depth: number;
    isSelected: boolean;
    isLoading: boolean;
    isExpanded: boolean;
    hasChildren: boolean;
    isFirst: boolean;
    isLast: boolean;
    childCount?: number;
    isDisabled?: boolean;
    onSelect: (node: BadgeTreeNode) => void;
    onToggleExpand: (node: BadgeTreeNode) => void;
    onCreateRequest: (nodeType: BadgeNodeType, parentId: number) => void;
    onMoveUp: (node: BadgeTreeNode) => void;
    onMoveDown: (node: BadgeTreeNode) => void;
    renderChildren: () => React.ReactNode;
}

const TreeItem = ({
    node,
    depth,
    isSelected,
    isLoading,
    isExpanded,
    hasChildren,
    isFirst,
    isLast,
    childCount,
    isDisabled,
    onSelect,
    onToggleExpand,
    onCreateRequest,
    onMoveUp,
    onMoveDown,
    renderChildren
}: TreeItemProps) => {
    const image = getNodeImage(node);
    const imageUrl = image
        ? getCdnImage(image.bucket, image.key, {
              resize: { width: 32, height: 32 }
          })
        : undefined;
    const style = nodeStyles[node.nodeType];
    const IconComponent = style.icon;

    const getIcon = () => {
        if (imageUrl) {
            return (
                <img
                    src={imageUrl}
                    alt=""
                    className="size-[22px] rounded border object-cover"
                    onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                    }}
                />
            );
        }
        return (
            <div
                className={cn(
                    'flex size-6 items-center justify-center',
                    style.iconBg
                )}
            >
                <IconComponent className={cn('size-3.5', style.iconColor)} />
            </div>
        );
    };

    return (
        <div>
            <div
                role="button"
                tabIndex={0}
                onClick={() => onSelect(node)}
                onKeyDown={(e) => e.key === 'Enter' && onSelect(node)}
                style={{ paddingLeft: indentPx(depth) }}
                className={cn(
                    'group flex min-h-9 cursor-pointer items-center border-l-[3px] border-l-transparent py-1 pr-1 transition-colors',
                    style.hoverBg,
                    isSelected && [style.selectedBg, style.selectedBorder]
                )}
            >
                {hasChildren ? (
                    <Button
                        variant="ghost"
                        size="icon-xs"
                        className={cn('mr-1.5', isExpanded && 'bg-accent')}
                        onClick={(e) => {
                            e.stopPropagation();
                            onToggleExpand(node);
                        }}
                    >
                        {isLoading ? (
                            <Spinner size="sm" />
                        ) : isExpanded ? (
                            <ChevronUp />
                        ) : (
                            <ChevronDown />
                        )}
                    </Button>
                ) : (
                    <div className="w-7" />
                )}

                <div className="mr-2 flex items-center">{getIcon()}</div>

                <div className="flex min-w-0 flex-1 items-center gap-2">
                    <span
                        className={cn(
                            'truncate text-[13px]',
                            isSelected && 'font-semibold',
                            isDisabled && 'opacity-50'
                        )}
                    >
                        {node.label}
                    </span>
                    {isDisabled && (
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <EyeOff className="size-3.5 shrink-0 text-muted-foreground" />
                            </TooltipTrigger>
                            <TooltipContent>
                                Disabled - Hidden from users
                            </TooltipContent>
                        </Tooltip>
                    )}
                    {childCount !== undefined &&
                        childCount > 0 &&
                        !isExpanded && (
                            <Badge
                                variant="muted"
                                className="h-[18px] min-w-[18px] justify-center px-1 text-[10px] font-semibold"
                            >
                                {childCount}
                            </Badge>
                        )}
                </div>

                <div
                    className="ml-1 flex items-center opacity-0 transition-opacity group-hover:opacity-100"
                    onClick={(e) => e.stopPropagation()}
                >
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Button
                                variant="ghost"
                                size="icon-xs"
                                disabled={isFirst}
                                onClick={() => onMoveUp(node)}
                            >
                                <ChevronUp />
                            </Button>
                        </TooltipTrigger>
                        <TooltipContent>Move up</TooltipContent>
                    </Tooltip>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Button
                                variant="ghost"
                                size="icon-xs"
                                disabled={isLast}
                                onClick={() => onMoveDown(node)}
                            >
                                <ChevronDown />
                            </Button>
                        </TooltipTrigger>
                        <TooltipContent>Move down</TooltipContent>
                    </Tooltip>
                    {hasChildren && (
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Button
                                    variant="ghost"
                                    size="icon-xs"
                                    className="ml-0.5 text-primary"
                                    onClick={() =>
                                        onCreateRequest(
                                            node.nodeType === 'category'
                                                ? 'type'
                                                : 'level',
                                            node.entityId
                                        )
                                    }
                                >
                                    <Plus />
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                                {`Add ${node.nodeType === 'category' ? 'type' : 'level'}`}
                            </TooltipContent>
                        </Tooltip>
                    )}
                </div>
            </div>

            {hasChildren && (
                <Collapsible open={isExpanded}>
                    <CollapsibleContent>
                        <div className="relative">
                            <div
                                className="absolute top-0 bottom-0 w-px bg-border"
                                style={{ left: indentPx(depth) + 8 }}
                            />
                            {renderChildren()}
                        </div>
                    </CollapsibleContent>
                </Collapsible>
            )}
        </div>
    );
};

export const BadgeTree = observer(
    React.forwardRef<BadgeTreeHandle, BadgeTreeProps>(
        ({ onSelect, onCreateRequest, selectedNodeId }, ref) => {
            const { fitnessActivityClient } = injectTSDI(Api);
            const [searchQuery, setSearchQuery] = React.useState('');
            const [treeData, setTreeData] = React.useState<BadgeTreeNode[]>([]);
            const [expandedItems, setExpandedItems] = React.useState<
                Set<string>
            >(new Set());
            const [loadingNodes, setLoadingNodes] = React.useState<Set<string>>(
                new Set()
            );
            const [isSaving, setIsSaving] = React.useState(false);

            const treeDataRef = React.useRef(treeData);
            React.useEffect(() => {
                treeDataRef.current = treeData;
            }, [treeData]);

            const categoriesRequest = useWrapRequest(async () => {
                const result =
                    await fitnessActivityClient.admin.badgeCategoryAdminControllerGetCategories(
                        {
                            size: 100,
                            sort: ['sortOrder']
                        }
                    );
                return result.data.content || [];
            });

            React.useEffect(() => {
                categoriesRequest.request([]);
            }, []);

            React.useEffect(() => {
                if (categoriesRequest.result) {
                    setTreeData(
                        categoriesRequest.result.map((cat) => ({
                            id: `category-${cat.id}`,
                            label: getLocalizedName(cat.name),
                            nodeType: 'category' as BadgeNodeType,
                            entityId: cat.id,
                            data: cat,
                            children: undefined,
                            enabled: cat.enabled ?? true
                        }))
                    );
                }
            }, [categoriesRequest.result]);

            const loadTypesForCategory = async (
                categoryId: number
            ): Promise<BadgeTreeNode[]> => {
                const result =
                    await fitnessActivityClient.admin.badgeTypeAdminControllerGetTypes(
                        {
                            categoryId,
                            size: 100,
                            sort: ['sortOrder']
                        }
                    );
                return (result.data.content || []).map((type) => ({
                    id: `type-${type.id}`,
                    label: getLocalizedName(type.name),
                    nodeType: 'type' as BadgeNodeType,
                    entityId: type.id,
                    parentId: categoryId,
                    data: type,
                    children: undefined
                }));
            };

            const loadLevelsForType = async (
                typeId: number
            ): Promise<BadgeTreeNode[]> => {
                const result =
                    await fitnessActivityClient.admin.badgeLevelAdminControllerGetLevels(
                        {
                            badgeTypeId: typeId,
                            size: 100,
                            sort: ['levelIndex']
                        }
                    );
                return (result.data.content || []).map((level) => ({
                    id: `level-${level.id}`,
                    label: `${level.levelIndex}. ${getLocalizedName(level.name)}`,
                    nodeType: 'level' as BadgeNodeType,
                    entityId: level.id,
                    parentId: typeId,
                    data: level
                }));
            };

            const handleToggleExpand = async (node: BadgeTreeNode) => {
                const itemId = node.id;
                if (expandedItems.has(itemId)) {
                    setExpandedItems((prev) => {
                        const next = new Set(prev);
                        next.delete(itemId);
                        return next;
                    });
                } else {
                    setExpandedItems((prev) => new Set(prev).add(itemId));
                    if (!node.children) {
                        setLoadingNodes((prev) => new Set(prev).add(itemId));
                        try {
                            const children =
                                node.nodeType === 'category'
                                    ? await loadTypesForCategory(node.entityId)
                                    : await loadLevelsForType(node.entityId);
                            setTreeData((prev) =>
                                updateNodeChildren(prev, itemId, children)
                            );
                        } finally {
                            setLoadingNodes((prev) => {
                                const next = new Set(prev);
                                next.delete(itemId);
                                return next;
                            });
                        }
                    }
                }
            };

            const findNode = (
                nodes: BadgeTreeNode[],
                id: string
            ): BadgeTreeNode | null => {
                for (const node of nodes) {
                    if (node.id === id) return node;
                    if (node.children) {
                        const found = findNode(node.children, id);
                        if (found) return found;
                    }
                }
                return null;
            };

            const updateNodeChildren = (
                nodes: BadgeTreeNode[],
                targetId: string,
                children: BadgeTreeNode[]
            ): BadgeTreeNode[] => {
                return nodes.map((node) => {
                    if (node.id === targetId) return { ...node, children };
                    if (node.children)
                        return {
                            ...node,
                            children: updateNodeChildren(
                                node.children,
                                targetId,
                                children
                            )
                        };
                    return node;
                });
            };

            const updateNodeInPlace = (
                nodes: BadgeTreeNode[],
                targetId: string,
                updater: (node: BadgeTreeNode) => BadgeTreeNode
            ): BadgeTreeNode[] => {
                return nodes.map((node) => {
                    if (node.id === targetId) return updater(node);
                    if (node.children)
                        return {
                            ...node,
                            children: updateNodeInPlace(
                                node.children,
                                targetId,
                                updater
                            )
                        };
                    return node;
                });
            };

            const removeNodeFromTree = (
                nodes: BadgeTreeNode[],
                targetId: string
            ): BadgeTreeNode[] => {
                return nodes
                    .filter((node) => node.id !== targetId)
                    .map((node) => {
                        if (node.children)
                            return {
                                ...node,
                                children: removeNodeFromTree(
                                    node.children,
                                    targetId
                                )
                            };
                        return node;
                    });
            };

            const addNodeToParent = (
                nodes: BadgeTreeNode[],
                parentId: string | undefined,
                newNode: BadgeTreeNode
            ): BadgeTreeNode[] => {
                if (!parentId) return [...nodes, newNode];
                return nodes.map((node) => {
                    if (node.id === parentId)
                        return {
                            ...node,
                            children: [...(node.children || []), newNode]
                        };
                    if (node.children)
                        return {
                            ...node,
                            children: addNodeToParent(
                                node.children,
                                parentId,
                                newNode
                            )
                        };
                    return node;
                });
            };

            const saveNodeOrder = async (
                nodeType: BadgeNodeType,
                nodes: BadgeTreeNode[]
            ) => {
                setIsSaving(true);
                try {
                    for (let i = 0; i < nodes.length; i++) {
                        const node = nodes[i];
                        if (nodeType === 'category') {
                            await fitnessActivityClient.admin.badgeCategoryAdminControllerUpdateCategory(
                                node.entityId,
                                { sortOrder: i }
                            );
                        } else if (nodeType === 'type') {
                            await fitnessActivityClient.admin.badgeTypeAdminControllerUpdateType(
                                node.entityId,
                                { sortOrder: i }
                            );
                        } else if (nodeType === 'level') {
                            await fitnessActivityClient.admin.badgeLevelAdminControllerUpdateLevel(
                                node.entityId,
                                { levelIndex: i + 1 }
                            );
                        }
                    }
                } catch (error) {
                    toast.error('Failed to save order');
                    categoriesRequest.request([]);
                } finally {
                    setIsSaving(false);
                }
            };

            const handleMoveUp = async (node: BadgeTreeNode) => {
                const currentTree = treeDataRef.current;
                if (node.nodeType === 'category') {
                    const idx = currentTree.findIndex((n) => n.id === node.id);
                    if (idx > 0) {
                        const newTree = arrayMove(currentTree, idx, idx - 1);
                        setTreeData(newTree);
                        treeDataRef.current = newTree;
                        await saveNodeOrder('category', newTree);
                    }
                } else {
                    const parentId =
                        node.nodeType === 'type'
                            ? `category-${node.parentId}`
                            : `type-${node.parentId}`;
                    const parent = findNode(currentTree, parentId);
                    if (!parent?.children) return;
                    const idx = parent.children.findIndex(
                        (n) => n.id === node.id
                    );
                    if (idx <= 0) return;
                    const newChildren = arrayMove(
                        parent.children,
                        idx,
                        idx - 1
                    );
                    const updatedChildren =
                        node.nodeType === 'level'
                            ? newChildren.map((c, i) => ({
                                  ...c,
                                  label: `${i + 1}. ${getLocalizedName((c.data as BadgeLevelAdminDto).name)}`
                              }))
                            : newChildren;
                    const newTree = updateNodeChildren(
                        currentTree,
                        parentId,
                        updatedChildren
                    );
                    setTreeData(newTree);
                    treeDataRef.current = newTree;
                    await saveNodeOrder(node.nodeType, updatedChildren);
                }
            };

            const handleMoveDown = async (node: BadgeTreeNode) => {
                const currentTree = treeDataRef.current;
                if (node.nodeType === 'category') {
                    const idx = currentTree.findIndex((n) => n.id === node.id);
                    if (idx < currentTree.length - 1) {
                        const newTree = arrayMove(currentTree, idx, idx + 1);
                        setTreeData(newTree);
                        treeDataRef.current = newTree;
                        await saveNodeOrder('category', newTree);
                    }
                } else {
                    const parentId =
                        node.nodeType === 'type'
                            ? `category-${node.parentId}`
                            : `type-${node.parentId}`;
                    const parent = findNode(currentTree, parentId);
                    if (!parent?.children) return;
                    const idx = parent.children.findIndex(
                        (n) => n.id === node.id
                    );
                    if (idx >= parent.children.length - 1) return;
                    const newChildren = arrayMove(
                        parent.children,
                        idx,
                        idx + 1
                    );
                    const updatedChildren =
                        node.nodeType === 'level'
                            ? newChildren.map((c, i) => ({
                                  ...c,
                                  label: `${i + 1}. ${getLocalizedName((c.data as BadgeLevelAdminDto).name)}`
                              }))
                            : newChildren;
                    const newTree = updateNodeChildren(
                        currentTree,
                        parentId,
                        updatedChildren
                    );
                    setTreeData(newTree);
                    treeDataRef.current = newTree;
                    await saveNodeOrder(node.nodeType, updatedChildren);
                }
            };

            React.useImperativeHandle(
                ref,
                () => ({
                    updateNode: (nodeId, data) => {
                        const name = data.name as Record<string, string>;
                        const label =
                            'levelIndex' in data
                                ? `${(data as BadgeLevelAdminDto).levelIndex}. ${getLocalizedName(name)}`
                                : getLocalizedName(name);
                        setTreeData((prev) =>
                            updateNodeInPlace(prev, nodeId, (node) => ({
                                ...node,
                                label,
                                data
                            }))
                        );
                    },
                    addNode: (node, parentId) => {
                        setTreeData((prev) =>
                            addNodeToParent(prev, parentId, node)
                        );
                        if (parentId && !expandedItems.has(parentId)) {
                            setExpandedItems((prev) =>
                                new Set(prev).add(parentId)
                            );
                        }
                        onSelect(node);
                    },
                    removeNode: (nodeId) => {
                        setTreeData((prev) => removeNodeFromTree(prev, nodeId));
                    },
                    selectNode: (nodeId) => {
                        const node = findNode(treeData, nodeId);
                        if (node) onSelect(node);
                    }
                }),
                [treeData, expandedItems, onSelect]
            );

            const filterTree = (
                nodes: BadgeTreeNode[],
                query: string
            ): BadgeTreeNode[] => {
                if (!query) return nodes;
                const lowerQuery = query.toLowerCase();
                const result: BadgeTreeNode[] = [];
                for (const node of nodes) {
                    const matches = node.label
                        .toLowerCase()
                        .includes(lowerQuery);
                    const filteredChildren = node.children
                        ? filterTree(node.children, query)
                        : [];
                    if (matches || filteredChildren.length > 0) {
                        result.push({
                            ...node,
                            children:
                                filteredChildren.length > 0
                                    ? filteredChildren
                                    : node.children
                        });
                    }
                }
                return result;
            };

            const filteredData = filterTree(treeData, searchQuery);

            const renderTreeItems = (nodes: BadgeTreeNode[], depth: number) => (
                <div className={depth === 0 ? 'py-1' : undefined}>
                    {nodes.map((node, idx) => (
                        <TreeItem
                            key={node.id}
                            node={node}
                            depth={depth}
                            isSelected={selectedNodeId === node.id}
                            isLoading={loadingNodes.has(node.id)}
                            isExpanded={expandedItems.has(node.id)}
                            hasChildren={node.nodeType !== 'level'}
                            isFirst={idx === 0}
                            isLast={idx === nodes.length - 1}
                            childCount={node.children?.length}
                            isDisabled={
                                node.nodeType === 'category' &&
                                node.enabled === false
                            }
                            onSelect={onSelect}
                            onToggleExpand={handleToggleExpand}
                            onCreateRequest={onCreateRequest}
                            onMoveUp={handleMoveUp}
                            onMoveDown={handleMoveDown}
                            renderChildren={() => {
                                if (loadingNodes.has(node.id)) {
                                    return (
                                        <div
                                            className="flex flex-col gap-1 py-1.5"
                                            style={{
                                                paddingLeft: indentPx(depth + 1)
                                            }}
                                        >
                                            <Skeleton className="h-8 w-[70%]" />
                                            <Skeleton className="h-8 w-[55%]" />
                                        </div>
                                    );
                                }
                                if (node.children && node.children.length > 0) {
                                    return renderTreeItems(
                                        node.children,
                                        depth + 1
                                    );
                                }
                                return (
                                    <div
                                        className="flex items-center gap-2 py-2"
                                        style={{
                                            paddingLeft: indentPx(depth + 1)
                                        }}
                                    >
                                        <span className="text-xs text-muted-foreground italic">
                                            No{' '}
                                            {node.nodeType === 'category'
                                                ? 'types'
                                                : 'levels'}{' '}
                                            yet
                                        </span>
                                        <Button
                                            variant="outline"
                                            size="xs"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onCreateRequest(
                                                    node.nodeType === 'category'
                                                        ? 'type'
                                                        : 'level',
                                                    node.entityId
                                                );
                                            }}
                                        >
                                            {`Add ${node.nodeType === 'category' ? 'type' : 'level'}`}
                                        </Button>
                                    </div>
                                );
                            }}
                        />
                    ))}
                </div>
            );

            return (
                <div className="flex h-full flex-col">
                    <div className="flex items-center gap-2 border-b p-3">
                        <div className="relative flex-1">
                            <Search className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground" />
                            <Input
                                placeholder="Search badges..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="h-9 pl-8 text-[13px]"
                            />
                        </div>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Button
                                    size="icon-sm"
                                    onClick={() => onCreateRequest('category')}
                                >
                                    <Plus />
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent>Add new category</TooltipContent>
                        </Tooltip>
                        {isSaving && <Spinner size="sm" />}
                    </div>

                    <div className="flex-1 overflow-auto">
                        {categoriesRequest.loading ? (
                            <div className="flex flex-col gap-2 p-4">
                                <Skeleton className="h-9" />
                                <Skeleton className="h-9 w-[85%]" />
                                <Skeleton className="h-9 w-[70%]" />
                            </div>
                        ) : filteredData.length === 0 ? (
                            <div className="flex flex-col items-center gap-4 p-8 text-center">
                                {searchQuery ? (
                                    <>
                                        <Search className="size-10 text-muted-foreground" />
                                        <p className="text-sm text-muted-foreground">
                                            No badges match "{searchQuery}"
                                        </p>
                                    </>
                                ) : (
                                    <>
                                        <div className="flex size-16 items-center justify-center rounded-lg bg-primary/10">
                                            <Trophy className="size-8 text-primary" />
                                        </div>
                                        <div className="flex flex-col gap-1">
                                            <p className="text-sm font-medium">
                                                No badge categories yet
                                            </p>
                                            <p className="text-xs text-muted-foreground">
                                                Create your first category to
                                                start organizing badges
                                            </p>
                                        </div>
                                        <Button
                                            size="sm"
                                            onClick={() =>
                                                onCreateRequest('category')
                                            }
                                        >
                                            <Plus />
                                            Add Category
                                        </Button>
                                    </>
                                )}
                            </div>
                        ) : (
                            renderTreeItems(filteredData, 0)
                        )}
                    </div>
                </div>
            );
        }
    )
);

export default BadgeTree;
